import React, { useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, FileSpreadsheet, FileText, UserPlus } from 'lucide-react';

const plain = (s) => (s || '').replace(/<[^>]+>/g, '').trim();

/** Everything still empty across the response: undrafted narrative sections and blank (non-pricing) answers, grouped by document. */
export const computeGaps = (project) => {
  const groups = [];
  (project.deliverables || []).filter((d) => ['narrative', 'generated'].includes(d.type)).forEach((d) => {
    const items = (d.sections || []).map((s, i) => ({ s, i })).filter(({ s }) => !plain(s.draft) || (s.draft || '').includes('[GAP'))
      .map(({ s, i }) => ({ kind: 'section', did: d.id, sid: s.id, index: i, label: `${s.number ? `${s.number} ` : ''}${s.heading}`, reason: plain(s.draft) ? 'gap marker left' : 'not drafted', assignee: s.assigned_to || '' }));
    groups.push({ id: d.id, kind: 'narrative', title: d.title, total: (d.sections || []).length, items });
  });
  (project.documents || []).filter((d) => Array.isArray(d.questions)).forEach((d) => {
    const items = d.questions.filter((q) => !q.is_pricing && !plain(q.response))
      .map((q) => ({ kind: 'question', docId: d.id, idx: q.idx, label: `${q.no || q.idx + 1}. ${q.text}`, reason: q.section || 'blank answer', assignee: q.assigned_to || '' }));
    groups.push({ id: d.id, kind: 'sheet', title: d.filename, total: d.questions.filter((q) => !q.is_pricing).length, items });
  });
  return { groups, count: groups.reduce((n, g) => n + g.items.length, 0) };
};

const SHOW = 40;
const short = (email) => (email || '').split('@')[0];

/** One "assign to…" control per empty line — hands the blank out without leaving the preview. */
const AssignPick = ({ it, assignees, onAssign, testId }) => {
  const [busy, setBusy] = useState(false);
  const change = async (e) => { e.stopPropagation(); setBusy(true); try { await onAssign(it, e.target.value); } finally { setBusy(false); } };
  return (
    <span className="inline-flex items-center gap-0.5 shrink-0" title={it.assignee ? `Assigned to ${it.assignee}` : 'Assign to…'} onClick={(e) => e.stopPropagation()}>
      <UserPlus className={`w-3 h-3 ${it.assignee ? 'text-[#0F2040]' : 'text-slate-300'}`} />
      <select value={it.assignee} onChange={change} disabled={busy} className={`text-[10px] bg-transparent border-0 max-w-[88px] truncate focus:outline-none cursor-pointer ${it.assignee ? 'text-[#0F2040] font-medium' : 'text-slate-400'}`} data-testid={testId}>
        <option value="">assign to…</option>
        {assignees.map((a) => <option key={a.email} value={a.email}>{a.name || short(a.email)}</option>)}
        {it.assignee && !assignees.find((a) => a.email === it.assignee) && <option value={it.assignee}>{short(it.assignee)}</option>}
      </select>
    </span>
  );
};

/** "What's still empty" — sits beside the preview; every line jumps straight to that section or answer, and can be handed to someone. */
export const PreviewGaps = ({ project, onJump, assignees = [], onAssign }) => {
  const { groups, count } = useMemo(() => computeGaps(project), [project]);
  const [open, setOpen] = useState({});
  const [more, setMore] = useState({});
  const isOpen = (g) => (open[g.id] === undefined ? true : open[g.id]);
  const unassigned = groups.reduce((n, g) => n + g.items.filter((x) => !x.assignee).length, 0);
  return (
    <aside className="rounded-lg border border-slate-200 bg-white p-3 space-y-2" data-testid="rfp-preview-gaps">
      <div className="flex items-center gap-1.5">
        {count ? <AlertTriangle className="w-3.5 h-3.5 text-amber-500" /> : <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />}
        <span className="text-xs font-semibold text-[#0F2040]">What's still empty</span>
        <span className={`ml-auto text-[10px] font-bold rounded-full px-1.5 ${count ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-700'}`} data-testid="rfp-preview-gaps-count">{count}</span>
      </div>
      {!count && <p className="text-[11px] text-slate-500" data-testid="rfp-preview-gaps-none">Every section is drafted and every answer has text. Read it through, then build the package.</p>}
      {count > 0 && onAssign && <p className="text-[10px] text-slate-400" data-testid="rfp-preview-gaps-unassigned">{unassigned ? `${unassigned} not handed to anyone yet` : 'Every blank has an owner'}</p>}
      {groups.filter((g) => g.items.length).map((g) => (
        <div key={g.id} className="border border-slate-100 rounded-md" data-testid={`rfp-preview-gaps-group-${g.id}`}>
          <button type="button" onClick={() => setOpen((m) => ({ ...m, [g.id]: !isOpen(g) }))} className="w-full flex items-center gap-1.5 px-2 py-1.5 text-left hover:bg-slate-50" data-testid={`rfp-preview-gaps-toggle-${g.id}`}>
            {isOpen(g) ? <ChevronDown className="w-3 h-3 text-slate-400" /> : <ChevronRight className="w-3 h-3 text-slate-400" />}
            {g.kind === 'narrative' ? <FileText className="w-3 h-3 text-indigo-500 shrink-0" /> : <FileSpreadsheet className="w-3 h-3 text-emerald-600 shrink-0" />}
            <span className="text-[11px] font-medium text-slate-700 truncate flex-1">{g.title}</span>
            <span className="text-[10px] text-amber-700 shrink-0">{g.items.length} of {g.total}</span>
          </button>
          {isOpen(g) && (
            <ul className="border-t border-slate-100 divide-y divide-slate-50">
              {g.items.slice(0, more[g.id] ? g.items.length : SHOW).map((it, k) => (
                <li key={it.sid || it.idx} className="flex items-start gap-1 px-2 py-1 hover:bg-amber-50 group">
                  <button type="button" onClick={() => onJump(it)} className="min-w-0 flex-1 text-left" title="Jump to it" data-testid={`rfp-preview-gap-${g.id}-${k}`}>
                    <span className="block text-[11px] text-slate-700 truncate group-hover:text-[#0F2040]">{it.label}</span>
                    <span className="block text-[9px] text-slate-400 truncate">{it.reason}</span>
                  </button>
                  {onAssign && <AssignPick it={it} assignees={assignees} onAssign={onAssign} testId={`rfp-preview-gap-assign-${g.id}-${k}`} />}
                </li>
              ))}
              {g.items.length > SHOW && !more[g.id] && (
                <li><button type="button" onClick={() => setMore((m) => ({ ...m, [g.id]: true }))} className="w-full text-[10px] text-[#0F2040] underline py-1" data-testid={`rfp-preview-gaps-more-${g.id}`}>and {g.items.length - SHOW} more…</button></li>
              )}
            </ul>
          )}
        </div>
      ))}
    </aside>
  );
};
